import { useEffect } from 'react'

const SELECTOR = '[data-animate]'
const VISIBLE_CLASS = 'is-visible'
const TRIGGER_LINE = 0.9

export function useScrollReveal() {
  useEffect(() => {
    const reveal = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) show(entry.target)
        }
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -10% 0px',
      },
    )

    function show(el: Element) {
      if (el.classList.contains(VISIBLE_CLASS)) return
      el.classList.add(VISIBLE_CLASS)
      // Reveal is one-way: stop watching so it never replays on scroll-back.
      reveal.unobserve(el)
    }

    function register(el: Element) {
      if (el.classList.contains(VISIBLE_CLASS)) return
      const delay = el.getAttribute('data-animate-delay')
      if (delay) (el as HTMLElement).style.setProperty('--animate-delay', delay)
      reveal.observe(el)
    }

    function watch(node: Element) {
      if (node.matches(SELECTOR)) register(node)
      node.querySelectorAll(SELECTOR).forEach(register)
    }

    function sweep() {
      const pending = document.querySelectorAll(`${SELECTOR}:not(.${VISIBLE_CLASS})`)
      for (const el of pending) {
        const rect = el.getBoundingClientRect()
        if (rect.top < window.innerHeight * TRIGGER_LINE && rect.bottom > 0) show(el)
      }
    }


    let queued = false
    let lastSweep = 0
    function queueSweep() {
      if (document.hidden) {
        const now = performance.now()
        if (now - lastSweep < 100) return
        lastSweep = now
        sweep()
        return
      }
      if (queued) return
      queued = true
      requestAnimationFrame(() => {
        queued = false
        sweep()
      })
    }

    watch(document.body)
    queueSweep()

    const added = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) watch(node)
        }
      }
      queueSweep()
    })
    added.observe(document.body, { childList: true, subtree: true })

    window.addEventListener('scroll', queueSweep, { passive: true })
    window.addEventListener('resize', queueSweep, { passive: true })
    document.addEventListener('visibilitychange', queueSweep)

    return () => {
      reveal.disconnect()
      added.disconnect()
      window.removeEventListener('scroll', queueSweep)
      window.removeEventListener('resize', queueSweep)
      document.removeEventListener('visibilitychange', queueSweep)
    }
  }, [])
}
